import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  FormControlProps,
  FormLabelProps,
  FormErrorMessageProps,
  FormHelperTextProps,
  Box,
  BoxProps,
  InputProps
} from '@chakra-ui/react';
import React from 'react';

export interface DirectionProp {
  direction?: 'row' | 'column';
}

interface ICustomFormControl extends DirectionProp {
  id?: string;
  size?: InputProps['size'];
  controlProps?: FormControlProps;
  children?: React.ReactNode;
  inputContainerProps?: BoxProps;
  label?: React.ReactNode;
  labelProps?: FormLabelProps;
  subLabel?: React.ReactNode;
  subLabelProps?: FormHelperTextProps;
  error?: React.ReactNode;
  errorProps?: FormErrorMessageProps;
  helperText?: React.ReactNode;
  helperProps?: FormHelperTextProps;
  forwardedRef?: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null | undefined;
}

const CustomFormControl = (props: ICustomFormControl) => {
  const {
    id,
    direction = 'column',
    size,
    controlProps,
    children,
    inputContainerProps,
    label,
    labelProps,
    subLabel,
    subLabelProps,
    error,
    errorProps,
    helperText,
    helperProps
  } = props;

  const isRow = direction === 'row';

  const SubLabelText = (props: FormHelperTextProps) => {
    return (
      <FormHelperText fontWeight={'normal'} {...props} {...subLabelProps}>
        {React.isValidElement(subLabel) ? subLabel : <div dangerouslySetInnerHTML={{ __html: subLabel.toString() }} />}
      </FormHelperText>
    );
  };

  const HelperText = (props: FormHelperTextProps) => {
    return (
      <FormHelperText {...props} {...helperProps}>
        <div dangerouslySetInnerHTML={{ __html: helperText.toString() }} />
      </FormHelperText>
    );
  };

  return (
    <FormControl
      data-selector={`form-field-${id}`}
      display={isRow ? 'flex' : null}
      rowGap={isRow ? 4 : null}
      {...controlProps}
    >
      {label && (
        <FormLabel
          display={!isRow ? 'flex' : null}
          data-selector={`${id}-label`}
          flexShrink={0}
          width={isRow ? { base: '14rem', '2xl': '18rem' } : null}
          pt={isRow ? 2 : null}
          fontSize={size}
          {...labelProps}
        >
          {label}

          {isRow && subLabel && <SubLabelText />}
        </FormLabel>
      )}

      {!isRow && subLabel && <SubLabelText mb={3} />}

      <Box flexGrow={1} maxWidth={isRow ? '30rem' : null} rowGap={isRow ? 4 : null} {...inputContainerProps}>
        {children}

        {helperText && <HelperText {...helperProps}>{helperText}</HelperText>}

        {error && (
          <FormErrorMessage data-selector={`${id}-error-text`} {...errorProps}>
            {error}
          </FormErrorMessage>
        )}
      </Box>
    </FormControl>
  );
};

export { CustomFormControl };
